import React from "react";
import PropTypes from "prop-types";
import { RadioGroupProps, RadioProps } from "./Radio.types";
import * as S from "./Radio.styles";

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>((props, ref) => {
    const { children, variant, disabled, error } = props;

    const RenderChildren = React.Children.map<React.ReactNode, React.ReactNode>(children, (child) =>
        React.isValidElement(child)
            ? React.cloneElement(child, {
                name: props.name,
                onChange: props.onChange,
                variant,
                disabled,
                error,
                ref
            })
            : null
    );

    return <S.RadioContainer>{RenderChildren}</S.RadioContainer>;
});

RadioGroup.defaultProps = {
    variant: "noBorder"
};

RadioGroup.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.element.isRequired).isRequired,
        PropTypes.element.isRequired
    ]).isRequired,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    variant: PropTypes.oneOf(["withBorder", "noBorder"]),
    disabled: PropTypes.bool,
    error: PropTypes.bool
};

RadioGroup.displayName = "RadioGroup";

/**
 * Renders regular `<input type="radio">` component with its all default attributes
 */
const Radio = React.forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
    const { children, variant, disabled, error, ...rest } = props;

    return (
        <S.RadioLabel tabIndex={disabled ? -1 : 0}>
            <S.RadioInput type="radio" ref={ref} variant={variant} disabled={disabled} error={error} {...rest} />
            <S.RadioSpan variant={variant} error={error}>
                {children}
            </S.RadioSpan>
        </S.RadioLabel>
    );
});

Radio.defaultProps = {
    variant: "noBorder"
};

Radio.propTypes = {
    variant: PropTypes.oneOf(["withBorder", "noBorder"]),
    error: PropTypes.bool
};

Radio.displayName = "Radio";

export { Radio, RadioGroup };
